import type { TFunction } from 'i18next';

type BadgeVariant = 'success' | 'info' | 'warning' | 'destructive' | 'secondary' | 'default';

/** Known API / OCPP spellings → locale key suffix (snake_case). */
const STATUS_KEY_ALIASES: Record<string, string> = {
  suspendedev: 'suspended_ev',
  suspended_ev: 'suspended_ev',
  suspendedevse: 'suspended_evse',
  suspended_evse: 'suspended_evse',
  started: 'started',
  preparing: 'preparing',
  charging: 'charging',
  finishing: 'finishing',
  completed: 'completed',
  stopped: 'stopped',
  stopping: 'stopping',
  aborted: 'aborted',
  cancelled: 'cancelled',
  canceled: 'cancelled',
  faulted: 'faulted',
  available: 'available',
  unavailable: 'unavailable',
  offline: 'offline',
  occupied: 'occupied',
  reserved: 'reserved',
  suspended: 'suspended',
  active: 'active',
  pending: 'pending',
  unknown: 'unknown',
};

/** OCPP measurand strings → locale key under measurandType.* */
const MEASURAND_KEY_ALIASES: Record<string, string> = {
  'Energy.Active.Import.Register': 'energy_active_import',
  'Energy.Active.Export.Register': 'energy_active_export',
  'Energy.Reactive.Import.Register': 'energy_reactive_import',
  'Energy.Reactive.Export.Register': 'energy_reactive_export',
  'Energy.Active.Import.Interval': 'energy_active_import_interval',
  'Energy.Active.Export.Interval': 'energy_active_export_interval',
  'Power.Active.Import': 'power_active_import',
  'Power.Active.Export': 'power_active_export',
  'Power.Reactive.Import': 'power_reactive_import',
  'Power.Reactive.Export': 'power_reactive_export',
  'Power.Offered': 'power_offered',
  'Voltage': 'voltage',
  'Current.Import': 'current_import',
  'Current.Export': 'current_export',
  'Current.Offered': 'current_offered',
  'Frequency': 'frequency',
  'Temperature': 'temperature',
  'SoC': 'soc',
  'RPM': 'rpm',
};

/** PascalCase / snake_case / compact API values → locale key suffix. */
export function toStatusKey(raw: string): string {
  if (!raw?.trim()) return 'unknown';
  const trimmed = raw.trim();
  const compact = trimmed.replace(/\s+/g, '').toLowerCase();
  if (STATUS_KEY_ALIASES[compact]) return STATUS_KEY_ALIASES[compact];

  if (/^[a-z]+(_[a-z0-9]+)*$/.test(trimmed)) return trimmed;

  return trimmed
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s.-]+/g, '_')
    .toLowerCase();
}

export function toMeasurandKey(raw: string): string {
  if (!raw?.trim()) return 'unknown';
  const trimmed = raw.trim();
  if (MEASURAND_KEY_ALIASES[trimmed]) return MEASURAND_KEY_ALIASES[trimmed];
  const compact = trimmed.replace(/\s+/g, '');
  if (MEASURAND_KEY_ALIASES[compact]) return MEASURAND_KEY_ALIASES[compact];
  return trimmed.replace(/\./g, '_').replace(/\s+/g, '_').toLowerCase();
}

function humanizeFallback(raw: string): string {
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function translateKey(t: TFunction, key: string, fallback: string): string {
  const out = t(key);
  if (out === key) return humanizeFallback(fallback);
  return out;
}

export function translateDeviceStatus(t: TFunction, status: string | undefined | null): string {
  if (!status) return translateKey(t, 'device.status.unknown', '—');
  const key = toStatusKey(status);
  return translateKey(t, `device.status.${key}`, status);
}

export function translateConnectorStatus(t: TFunction, status: string | undefined | null): string {
  return translateDeviceStatus(t, status);
}

export function translateSessionStatus(t: TFunction, status: string | undefined | null): string {
  if (!status) return '—';
  const key = toStatusKey(status);
  const sessionKey = `session.status.${key}`;
  const translated = t(sessionKey);
  if (translated !== sessionKey) return translated;
  return translateDeviceStatus(t, status);
}

export function translateMeasurand(t: TFunction, measurand: string | undefined | null): string {
  if (!measurand?.trim()) return '—';
  const key = toMeasurandKey(measurand);
  return translateKey(t, `measurandType.${key}`, measurand);
}

export function translateInvitationStatus(t: TFunction, status: string | undefined | null): string {
  if (!status) return '—';
  const key = toStatusKey(status);
  const invKey = `invitation.status.${key}`;
  const translated = t(invKey);
  if (translated !== invKey) return translated;
  return humanizeFallback(status);
}

export function translateStationType(t: TFunction, type: string | undefined | null): string {
  if (!type) return '—';
  const key = toStatusKey(type);
  const typeKey = `station.type.${key}`;
  return translateKey(t, typeKey, type);
}

export function translateStationMetaStatus(t: TFunction, status: string | undefined | null): string {
  if (!status) return '—';
  const key = toStatusKey(status);
  const metaKey = `station.metaStatus.${key}`;
  return translateKey(t, metaKey, status);
}

export function connectorStatusVariant(
  status: string | undefined | null,
): BadgeVariant {
  const normalized = toStatusKey(status ?? '');
  const map: Record<string, BadgeVariant> = {
    available: 'success',
    preparing: 'info',
    charging: 'info',
    suspended_ev: 'warning',
    suspended_evse: 'warning',
    suspended: 'warning',
    finishing: 'info',
    reserved: 'warning',
    unavailable: 'secondary',
    faulted: 'destructive',
    offline: 'secondary',
    occupied: 'info',
  };
  return map[normalized] ?? 'secondary';
}

export function sessionStatusVariant(status: string | undefined | null): BadgeVariant {
  const key = toStatusKey(status ?? '');
  if (key === 'charging' || key === 'active' || key === 'started' || key === 'preparing') return 'info';
  if (key === 'faulted' || key === 'failed' || key === 'aborted') return 'destructive';
  if (key === 'completed' || key === 'stopped' || key === 'finishing' || key === 'cancelled') return 'success';
  if (key === 'suspended_ev' || key === 'suspended_evse' || key === 'suspended') return 'warning';
  return 'secondary';
}

export function formatConnectorLabel(
  t: TFunction,
  connectorId: number,
  status: string | undefined | null,
): string {
  return t('connector.label', {
    id: connectorId,
    status: translateConnectorStatus(t, status),
  });
}
